/**
 * Withheld-narrative records (reviewer-voice quality gate, 2026-09-18).
 *
 * When the generation-time reviewer-voice gate (`applyReviewerVoiceQualityGate`
 * in `services/narrativeHygiene.ts`) trips on a narrative leaf, it no longer
 * writes a display string ("… unavailable — content failed quality gate.
 * Regenerate report.") into the report body. It WITHHOLDS the leaf (null; ''
 * for the top-level `summary`, whose string type is the body-loaded sentinel
 * across delivery) and records the leaf's exact path here, on the
 * `reviewer-voice-leak` quality warning.
 *
 * Every consumer that needs to know "which narrative was withheld" reads it
 * through this module:
 *   - render/export: `materializeWithheldNarrative` shows the honest
 *     "Not assessed in this report" treatment at that path;
 *   - frozen document (`buildReportDocumentModelV1`): a section whose only
 *     content was withheld is an explicit omission with honest detail; a
 *     withheld leaf inside other content stays visible in place as the notice;
 *   - decision analysis (`buildDecisionAnalysisFromReportV1`): a withheld key
 *     condition / recommendation freezes as the explicit notice marker, never
 *     dropped and never back-filled with plausible text;
 *   - section fallbacks and incremental carry-forward: a withheld path is
 *     never back-filled with synthesized text, nor carried without its record;
 *   - quality hold: a withheld leaf holds the report for revision (customer
 *     messages name the leaf LABEL). Delivery treats that hold like every other
 *     audit-advisory hold (owner decision 2026-09-18, "Allow export"): the
 *     report stays HELD and shows the notice at the leaf on every surface,
 *     exports included, but export and publishing are NOT blocked;
 *   - positive decision (Path to GO): unchanged by that decision. The report
 *     path still keeps a withheld report out of GO (see `reportPlan.ts`); gate
 *     mode still follows the frozen analysis, whose quality state is 'held'.
 *
 * Pure: no I/O, no React, no services.
 */
import type { SiteRagReport } from '../../types/siteRag';

/** One withheld narrative leaf, as persisted on the quality warning. */
export interface WithheldNarrativeLeaf {
  /** Dotted/indexed path of the leaf, e.g. `waterAnalysis.waterRightsAnalysis`
   *  or `developmentPathway[1].description`. */
  path: string;
  /** Reader label derived from the LEAF (never the container key). */
  label: string;
  /** Why the leaf was withheld. */
  reason: 'reviewer-voice-leak';
  /** Reviewer-voice pattern families that matched. */
  matchedPatterns: string[];
  /** First 200 chars of the withheld text (ops triage only; never rendered). */
  excerpt: string;
}

export type ReportLeafPathSegment = string | number;

/** Prefix of the quality-hold finding a withheld leaf stamps. */
export const WITHHELD_NARRATIVE_HOLD_FINDING_PREFIX = 'reviewerVoiceGate:';

/** `['waterAnalysis', 'recommendations', 1]` → `waterAnalysis.recommendations[1]`. */
export function formatReportLeafPath(segments: readonly ReportLeafPathSegment[]): string {
  let out = '';
  for (const segment of segments) {
    if (typeof segment === 'number') out += `[${segment}]`;
    else out += out ? `.${segment}` : segment;
  }
  return out;
}

/** Inverse of {@link formatReportLeafPath}. Returns null for an unparseable path. */
export function parseReportLeafPath(path: string): ReportLeafPathSegment[] | null {
  if (typeof path !== 'string' || !path.trim()) return null;
  const segments: ReportLeafPathSegment[] = [];
  const re = /([^.[\]]+)|\[(\d+)\]/g;
  let consumed = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(path)) !== null) {
    if (match[1] !== undefined) segments.push(match[1]);
    else segments.push(Number(match[2]));
    consumed += match[0].length;
  }
  // Separators ('.') are the only characters a well-formed path skips.
  const separators = (path.match(/\./g) ?? []).length;
  if (consumed + separators !== path.length || segments.length === 0) return null;
  return segments;
}

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b([a-z])/g, (c) => c.toUpperCase());
}

/**
 * Reader label for a leaf, derived from the LEAF key (the bug this replaces
 * labelled `waterAnalysis.waterRightsAnalysis` as "Water Analysis").
 * Array positions are 1-based in the label.
 */
export function reportLeafLabel(segments: readonly ReportLeafPathSegment[]): string {
  const lastKeyIndex = (() => {
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (typeof segments[i] === 'string') return i;
    }
    return -1;
  })();
  if (lastKeyIndex < 0) return 'Narrative';
  const label = humanizeKey(segments[lastKeyIndex] as string);
  const firstIndexAfterKey = segments.slice(lastKeyIndex + 1).find((s) => typeof s === 'number');
  if (typeof firstIndexAfterKey === 'number') return `${label} (item ${firstIndexAfterKey + 1})`;
  // An indexed parent (`developmentPathway[1].description`) names the parent
  // row so two withheld descriptions stay distinguishable.
  const parentIndex = segments[lastKeyIndex - 1];
  const parentKey = segments[lastKeyIndex - 2];
  if (typeof parentIndex === 'number' && typeof parentKey === 'string') {
    return `${humanizeKey(parentKey)} (item ${parentIndex + 1}): ${label}`;
  }
  return label;
}

function isWithheldLeaf(value: unknown): value is WithheldNarrativeLeaf {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.path === 'string' && row.path.trim().length > 0 && row.reason === 'reviewer-voice-leak';
}

/**
 * Every withheld narrative leaf recorded on the report, de-duplicated by path.
 * Legacy (pre-2026-09-18) `reviewer-voice-leak` records carry `sections`
 * instead — their bodies hold the old placeholder string, which the render
 * scrub handles — so they contribute nothing here.
 */
export function withheldNarrativeLeaves(
  report: Pick<SiteRagReport, 'qualityWarnings'> | null | undefined,
): WithheldNarrativeLeaf[] {
  const warnings = report?.qualityWarnings;
  if (!Array.isArray(warnings)) return [];
  const seen = new Set<string>();
  const out: WithheldNarrativeLeaf[] = [];
  for (const warning of warnings) {
    if (!warning || warning.type !== 'reviewer-voice-leak') continue;
    const withheld = (warning as { withheld?: unknown }).withheld;
    if (!Array.isArray(withheld)) continue;
    for (const leaf of withheld) {
      if (!isWithheldLeaf(leaf) || seen.has(leaf.path)) continue;
      seen.add(leaf.path);
      out.push(leaf);
    }
  }
  return out;
}

/**
 * The customer-facing treatment of a narrative the platform withheld. It
 * states what happened and how to read it; it does NOT claim an evidence gap
 * nobody measured (the gate fires on wording, not on evidence), and it tells
 * the reader the topic is OPEN, not cleared.
 */
export const WITHHELD_NARRATIVE_NOTICE =
  'Not assessed in this report — narrative withheld by an automated quality check; treat as open, not cleared.';

/**
 * Omission detail for a frozen-document section whose ONLY content was a
 * withheld leaf. Replaces "No citable content was present in the frozen
 * source report", which asserted an evidence gap nobody measured.
 */
export const WITHHELD_SECTION_OMISSION_DETAIL =
  'Narrative withheld by an automated quality check; not assessed in this report. Treat as open, not cleared.';

/**
 * What a quality finding says in place of withheld wording it reproduced.
 * Sanity findings are written BEFORE the reviewer-voice gate runs (the sanity
 * prompt quotes the opening of the executive summary, and the model echoes
 * it), and the DOCX/PPTX quality appendix and the CSV print them. The gate
 * keeps each finding and replaces only the reproduced span with this marker.
 */
export const WITHHELD_NARRATIVE_SPAN_MARKER = '[withheld narrative]';

/** True when an omission detail is the withheld-narrative detail. */
export function isWithheldSectionOmissionDetail(detail: unknown): boolean {
  return typeof detail === 'string' && detail.trim() === WITHHELD_SECTION_OMISSION_DETAIL;
}

/** The persisted quality-hold finding for a withheld leaf. Customer-visible
 *  (reader banner, DOCX/PPTX quality appendix): it names the LEAF label, never
 *  the raw JSON path or the matched pattern family (both stay on the
 *  `reviewer-voice-leak` record for ops triage). */
export function withheldNarrativeHoldFinding(leaf: WithheldNarrativeLeaf): string {
  return `${WITHHELD_NARRATIVE_HOLD_FINDING_PREFIX} ${leaf.label} was withheld by an automated quality check; it is not assessed in this report (treat as open, not cleared). Regenerate the report to restore it.`;
}

/** True when a stamped hold finding was minted for a withheld leaf. */
export function isWithheldNarrativeHoldFinding(finding: unknown): boolean {
  return typeof finding === 'string' && finding.startsWith(WITHHELD_NARRATIVE_HOLD_FINDING_PREFIX);
}

/**
 * True when the report withheld a narrative leaf, read from EITHER record:
 * the body's `reviewer-voice-leak` warning, or the hold finding that warning
 * stamps. The warning lives only on the full body; finalize persists the hold
 * findings on the report's metadata doc as well, so a read that holds only the
 * metadata row still sees the withhold.
 */
export function reportHasWithheldNarrative(
  report: Pick<SiteRagReport, 'qualityWarnings' | 'qualityHoldFindings'> | null | undefined,
): boolean {
  if (withheldNarrativeLeaves(report).length > 0) return true;
  const findings = report?.qualityHoldFindings;
  return Array.isArray(findings) && findings.some(isWithheldNarrativeHoldFinding);
}

/** The hold message for withheld leaves. Customer-visible (the reader's
 *  delivery banner): leaf labels, de-duplicated. It describes a HOLD, not a
 *  delivery block: since the 2026-09-18 owner decision a withheld leaf never
 *  bars export or publishing, so the message must not say it does. */
export function withheldNarrativeHoldMessage(leaves: readonly WithheldNarrativeLeaf[]): string {
  const labels = [...new Set(leaves.map((leaf) => leaf.label || 'Narrative'))].join(', ');
  return `Narrative withheld by an automated quality check (${labels}); the affected assessment is not in this report — treat it as open, not cleared. Regenerate the report to restore it and clear the hold.`;
}

/**
 * The delivery reason the 734f35d52 candidate code stamped for withheld leaves
 * ('hard-gate-failed', "… Regenerate the report before export or
 * publishing."). That code never reached production, but a revision finalized
 * under it carries the stamp immutably. Delivery reads the stamp as
 * audit-advisory, exactly like the historical truth-path stamps, so an old and
 * a new revision of the same withheld report get the same export decision.
 * Anchored to the exact retired wording. Labels may themselves contain
 * parentheses ("Development Pathway (item 2): Description"), hence the greedy
 * middle.
 */
const RETIRED_WITHHELD_DELIVERY_MESSAGE_RE =
  /^Narrative withheld by an automated quality check \(.+\); the affected assessment is not in this report\. Regenerate the report before export or publishing\.$/s;

/** True for the retired withheld-leaf delivery-block message (see above). */
export function isRetiredWithheldNarrativeDeliveryMessage(message: unknown): boolean {
  return typeof message === 'string' && RETIRED_WITHHELD_DELIVERY_MESSAGE_RE.test(message);
}

/**
 * The positive-decision (Path to GO) hold for a withheld leaf, and ONLY that.
 * Delivery no longer carries a reason for it, but Path to GO's report path
 * computed GO eligibility from that reason. Until the owner decides how a
 * withheld narrative counts toward GO, `reportPlan.ts` reinstates the hold
 * under this message so GO counting stays exactly where it was. The message
 * is never a delivery reason, and it sits outside every audit-advisory
 * predicate, so no advisory filter can drop it.
 */
export const WITHHELD_NARRATIVE_DECISION_HOLD_MESSAGE =
  'A narrative leaf was withheld by an automated quality check; the report path does not count this report toward GO until it is regenerated.';

function isEmptyLeaf(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/** Copy-on-write set of one EMPTY leaf. Returns the input when the path is
 *  unusable or the leaf already carries content. */
function setLeafIfEmpty(
  root: Record<string, unknown>,
  segments: readonly ReportLeafPathSegment[],
  replacement: string,
): Record<string, unknown> {
  const write = (node: unknown, depth: number): unknown => {
    const segment = segments[depth]!;
    const last = depth === segments.length - 1;
    if (typeof segment === 'number') {
      if (!Array.isArray(node) || segment >= node.length) return node;
      const current = node[segment];
      const next = last ? (isEmptyLeaf(current) ? replacement : current) : write(current, depth + 1);
      if (next === current) return node;
      const copy = node.slice();
      copy[segment] = next;
      return copy;
    }
    if (node === null || typeof node !== 'object' || Array.isArray(node)) return node;
    const record = node as Record<string, unknown>;
    const current = record[segment];
    if (!last && (current === null || typeof current !== 'object')) return node;
    const next = last ? (isEmptyLeaf(current) ? replacement : current) : write(current, depth + 1);
    if (next === current) return node;
    return { ...record, [segment]: next };
  };
  return write(root, 0) as Record<string, unknown>;
}

/**
 * Show {@link WITHHELD_NARRATIVE_NOTICE} at every leaf the reviewer-voice gate
 * withheld (recorded on the `reviewer-voice-leak` quality warning). Only an
 * EMPTY leaf is filled — content that legitimately replaced it later wins.
 * The section therefore stays visible as an open item instead of silently
 * disappearing (a missing water-rights assessment is a gating gap).
 *
 * A DERIVED view: never persist the result as the report body. Deterministic
 * in the report (the record travels on it), so a document or analysis built
 * from it rebuilds identically from the stored body.
 */
export function materializeWithheldNarrative<T extends Pick<SiteRagReport, 'qualityWarnings'>>(report: T): T {
  const leaves = withheldNarrativeLeaves(report);
  if (leaves.length === 0) return report;
  let out = report as unknown as Record<string, unknown>;
  for (const leaf of leaves) {
    const segments = parseReportLeafPath(leaf.path);
    if (!segments) continue;
    out = setLeafIfEmpty(out, segments, WITHHELD_NARRATIVE_NOTICE);
  }
  return out as unknown as T;
}
